import React from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Columns,
    Column,
    Dialog,
    Divider,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    Textarea,
    TooltipRenderer,
    IconRefresh,
    IconVisibility,
} from "braid-design-system";
import { useTranslation } from "react-i18next";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";
import { getPendingPromotions, approvePromotion, rejectPromotion, getAllCustomerGroups, PromoHedResponse } from "../services/promotionService";
import Loading from "../components/Loading";

const CRITERIA_LABELS: Record<string, string> = {
    item_group: "Item Group",
    item: "Item",
    amount_range: "Amount Range",
    payment_mode: "Payment Mode",
    location: "Location",
    customer_group: "Customer Group",
};

const getCriteriaLabel = (type: string): string => CRITERIA_LABELS[type] || type;

const formatCriteriaValue = (
    criteria: { criteria_type: string; criteria_value?: string | null; amount_min?: number | null; amount_max?: number | null },
    customerGroupMap: Record<string, string> = {}
): string => {
    if (criteria.criteria_type === "amount_range") {
        const min = criteria.amount_min != null ? criteria.amount_min : "any";
        const max = criteria.amount_max != null ? criteria.amount_max : "any";
        return `${min} - ${max}`;
    }
    const raw = criteria.criteria_value || "";
    if (!raw.trim()) return "None";
    if (criteria.criteria_type === "customer_group") {
        return raw
            .split(",")
            .map((v) => customerGroupMap[v.trim()] || v.trim())
            .join(", ");
    }
    return raw.split(",").map((v) => v.trim()).join(", ");
};

const formatDiscount = (promo: PromoHedResponse): string => {
    if (promo.promo_disper != null) return `${promo.promo_disper}%`;
    if (promo.promo_disamt != null) return String(promo.promo_disamt);
    if (promo.promo_free_item) return `Free item: ${promo.promo_free_item}`;
    if (promo.promo_free_voucher) return `Voucher: ${promo.promo_free_voucher}`;
    return "-";
};

const PromotionApprovalPanel: React.FC = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [promotions, setPromotions] = React.useState<PromoHedResponse[]>([]);
    const [customerGroupMap, setCustomerGroupMap] = React.useState<Record<string, string>>({});
    const [searchTerm, setSearchTerm] = React.useState("");
    const [viewingPromo, setViewingPromo] = React.useState<PromoHedResponse | null>(null);
    const [rejectingPromo, setRejectingPromo] = React.useState<PromoHedResponse | null>(null);
    const [rejectReason, setRejectReason] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);

    const fetchPendingPromotions = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Loading pending promotions...");
            const data = await getPendingPromotions();
            setPromotions(data);
        } catch (error: any) {
            toast.showError(`Error fetching pending promotions: ${error?.message || "Unknown error"}`);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        if (canManageMasters) {
            fetchPendingPromotions();
            getAllCustomerGroups()
                .then((groups) => {
                    const map: Record<string, string> = {};
                    groups.forEach((g) => {
                        if (g.id != null && g.group_name) {
                            map[String(g.id)] = g.group_name;
                        }
                    });
                    setCustomerGroupMap(map);
                })
                .catch(() => {});
        }
    }, [canManageMasters, fetchPendingPromotions]);

    const handleApprove = React.useCallback(async (promo: PromoHedResponse) => {
        if (promo.id == null) return;

        try {
            setSubmitting(true);
            await approvePromotion(promo.id);
            toast.showSuccess(`Promotion "${promo.promo_name}" approved successfully.`);
            setPromotions((prev) => prev.filter((p) => p.id !== promo.id));
            setViewingPromo((prev) => (prev?.id === promo.id ? null : prev));
        } catch (error: any) {
            toast.showError(`Error approving promotion: ${error?.message || "Unknown error"}`);
        } finally {
            setSubmitting(false);
        }
    }, [toast]);

    const openRejectDialog = React.useCallback((promo: PromoHedResponse) => {
        setRejectReason("");
        setRejectingPromo(promo);
    }, []);

    const closeRejectDialog = React.useCallback(() => {
        setRejectingPromo(null);
        setRejectReason("");
    }, []);

    const handleConfirmReject = React.useCallback(async () => {
        if (!rejectingPromo || rejectingPromo.id == null) return;

        try {
            setSubmitting(true);
            await rejectPromotion(rejectingPromo.id, rejectReason.trim() || undefined);
            toast.showSuccess(`Promotion "${rejectingPromo.promo_name}" rejected.`);
            setPromotions((prev) => prev.filter((p) => p.id !== rejectingPromo.id));
            setViewingPromo((prev) => (prev?.id === rejectingPromo.id ? null : prev));
            closeRejectDialog();
        } catch (error: any) {
            toast.showError(`Error rejecting promotion: ${error?.message || "Unknown error"}`);
        } finally {
            setSubmitting(false);
        }
    }, [rejectingPromo, rejectReason, toast, closeRejectDialog]);

    const filteredPromotions = React.useMemo(() => {
        if (!searchTerm.trim()) {
            return promotions;
        }
        const term = searchTerm.trim().toLowerCase();
        return promotions.filter((p) => {
            return (
                (p.promo_code || "").toLowerCase().includes(term) ||
                (p.promo_name || "").toLowerCase().includes(term) ||
                (p.promo_type_code || "").toLowerCase().includes(term)
            );
        });
    }, [promotions, searchTerm]);

    if (!canManageMasters) {
        return (
            <Box padding="large">
                <Alert tone="critical">
                    <Text>You do not have permission to access this page. Only administrators can review promotion approvals.</Text>
                </Alert>
            </Box>
        );
    }

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <Stack space="large">
                    <Columns space="large" collapseBelow="tablet" alignY="center">
                        <Column width="1/3">
                            <Box display="flex" justifyContent="flexStart" alignItems="center">
                                <Inline space="small" alignY="center">
                                    <Text weight="strong" size="large">
                                        {t("page.promotion_approval_panel.title", { defaultValue: "Pending Promotions" })}
                                    </Text>
                                    <Badge tone={promotions.length > 0 ? "caution" : "positive"}>
                                        {`${promotions.length} pending`}
                                    </Badge>
                                </Inline>
                            </Box>
                        </Column>
                        <Column width="2/3">
                            <Box display="flex" justifyContent="flexEnd">
                                <Inline space="small" alignY="center">
                                    {promotions.length > 0 ? (
                                        <TextField
                                            placeholder={t("page.promotion_approval_panel.search.placeholder", {
                                                defaultValue: "Search pending promotions...",
                                            })}
                                            aria-label={t("page.promotion_approval_panel.search.label", { defaultValue: "Search" })}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                                        />
                                    ) : null}
                                    <Button
                                        variant="ghost"
                                        tone="brandAccent"
                                        icon={<IconRefresh />}
                                        onClick={fetchPendingPromotions}
                                    >
                                        {t("page.promotion_approval_panel.button.refresh", { defaultValue: "Refresh" })}
                                    </Button>
                                </Inline>
                            </Box>
                        </Column>
                    </Columns>

                    {!loading && promotions.length === 0 ? (
                        <Alert tone="positive">
                            <Text>
                                {t("page.promotion_approval_panel.empty", {
                                    defaultValue: "There are no pending promotions awaiting approval.",
                                })}
                            </Text>
                        </Alert>
                    ) : (
                        <Table
                            label={t("page.promotion_approval_panel.table.label", {
                                defaultValue: "Pending Promotions",
                            })}
                        >
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.code", { defaultValue: "Code" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.name", { defaultValue: "Name" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.type", { defaultValue: "Type" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.discount", { defaultValue: "Discount" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.validity", { defaultValue: "Validity" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text>{t("page.promotion_approval_panel.table.headers.created_by", { defaultValue: "Created By" })}</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text>{t("page.promotion_approval_panel.table.headers.actions", { defaultValue: "Actions" })}</Text>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredPromotions.length > 0 ? (
                                    filteredPromotions.map((promo) => (
                                        <TableRow key={promo.id}>
                                            <TableCell>
                                                <Text weight="strong" tone="brandAccent">{promo.promo_code}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Stack space="xxsmall">
                                                    <Text weight="strong">{promo.promo_name}</Text>
                                                    {promo.promo_desc ? (
                                                        <Text size="small" tone="secondary">{promo.promo_desc}</Text>
                                                    ) : null}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Badge tone="info">{promo.promo_type_code || "-"}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Text size="small">{formatDiscount(promo)}</Text>
                                            </TableCell>
                                            <TableCell>
                                                {promo.promo_datefrom && promo.promo_dateto ? (
                                                    <Text size="small">
                                                        {promo.promo_datefrom} ~ {promo.promo_dateto}
                                                    </Text>
                                                ) : (
                                                    <Text size="small">-</Text>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Text size="small">{promo.promo_crby || "-"}</Text>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Inline space="small">
                                                    <TooltipRenderer
                                                        tooltip={<Text>{t("page.promotion_approval_panel.tooltip.view", { defaultValue: "View Details & Review" })}</Text>}
                                                    >
                                                        {({ triggerProps }) => (
                                                            <Box
                                                                onClick={() => setViewingPromo(promo)}
                                                                cursor="pointer"
                                                                {...triggerProps}
                                                            >
                                                                <IconVisibility tone="secondary" />
                                                            </Box>
                                                        )}
                                                    </TooltipRenderer>
                                                </Inline>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell align="center" colspan={7}>
                                            <Text tone="secondary">
                                                {searchTerm
                                                    ? t("page.promotion_approval_panel.no_results", { defaultValue: "No matching promotions found." })
                                                    : t("page.promotion_approval_panel.empty", { defaultValue: "No pending promotions." })}
                                            </Text>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </Box>

            <Dialog
                id="promotion-view-dialog"
                title={viewingPromo?.promo_name || "Promotion Details"}
                open={viewingPromo != null}
                onClose={() => setViewingPromo(null)}
                width="medium"
            >
                {viewingPromo ? (
                    <Stack space="large">
                        <Columns space="medium" collapseBelow="tablet">
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Code</Text>
                                    <Badge tone="info">{viewingPromo.promo_code}</Badge>
                                </Stack>
                            </Column>
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Type</Text>
                                    <Text weight="strong">{viewingPromo.promo_type_code || "-"}</Text>
                                </Stack>
                            </Column>
                            
                        </Columns>

                        {viewingPromo.promo_desc ? (
                            <Stack space="xsmall">
                                <Text size="small" tone="secondary">Description</Text>
                                <Text>{viewingPromo.promo_desc}</Text>
                            </Stack>
                        ) : null}

                        <Divider />

                        <Columns space="medium" collapseBelow="tablet">
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Validity</Text>
                                    <Text>
                                        {viewingPromo.promo_datefrom && viewingPromo.promo_dateto
                                            ? `${viewingPromo.promo_datefrom} ~ ${viewingPromo.promo_dateto}`
                                            : "-"}
                                    </Text>
                                    {viewingPromo.promo_timefrom || viewingPromo.promo_timeto ? (
                                        <Text size="small" tone="secondary">
                                            {viewingPromo.promo_timefrom || "00:00"} ~ {viewingPromo.promo_timeto || "23:59"}
                                        </Text>
                                    ) : null}
                                </Stack>
                            </Column>
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Discount / Offer</Text>
                                    <Badge tone="promote">{formatDiscount(viewingPromo)}</Badge>
                                </Stack>
                            </Column>
                        </Columns>

                        <Divider />

                        <Stack space="small">
                            <Text weight="strong" size="small">Offer Criteria</Text>
                            {viewingPromo.details && viewingPromo.details.length > 0 ? (
                                <Stack space="xsmall">
                                    {viewingPromo.details.map((detail, idx) => (
                                        <Inline key={idx} space="xsmall" alignY="center">
                                            <Text size="small" weight="strong">{getCriteriaLabel(detail.criteria_type)}:</Text>
                                            <Text size="small" tone="secondary">{formatCriteriaValue(detail, customerGroupMap)}</Text>
                                        </Inline>
                                    ))}
                                </Stack>
                            ) : (
                                <Text size="small" tone="secondary">No specific criteria defined (applies to all items)</Text>
                            )}
                        </Stack>

                        <Divider />

                        <Columns space="medium" collapseBelow="tablet">
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Created By</Text>
                                    <Text size="small">{viewingPromo.promo_crby || "-"}</Text>
                                </Stack>
                            </Column>
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Created At</Text>
                                    <Text size="small">{viewingPromo.promo_crat ? new Date(viewingPromo.promo_crat).toLocaleString() : "-"}</Text>
                                </Stack>
                            </Column>
                            <Column>
                                <Stack space="xsmall">
                                    <Text size="small" tone="secondary">Status</Text>
                                    <Inline space="xsmall">
                                        <Badge tone="caution">
                                            {viewingPromo.promo_status === "pending" ? "Pending Approval" : viewingPromo.promo_status}
                                        </Badge>
                                        <Badge tone={viewingPromo.promo_active ? "positive" : "neutral"}>
                                            {viewingPromo.promo_active ? "Active" : "Inactive"}
                                        </Badge>
                                    </Inline>
                                </Stack>
                            </Column>
                        </Columns>

                        <Box display="flex" justifyContent="flexEnd">
                            <Inline space="small">
                                <Button
                                    variant="solid"
                                    tone="critical"
                                    onClick={() => {
                                        const promo = viewingPromo;
                                        setViewingPromo(null);
                                        if (promo) openRejectDialog(promo);
                                    }}
                                    loading={submitting}
                                >
                                    {t("page.promotion_approval_panel.button.reject", { defaultValue: "Reject" })}
                                </Button>
                                <Button
                                    variant="solid"
                                    tone="brandAccent"
                                    onClick={() => viewingPromo && handleApprove(viewingPromo)}
                                    loading={submitting}
                                >
                                    {t("page.promotion_approval_panel.button.approve", { defaultValue: "Approve" })}
                                </Button>
                            </Inline>
                        </Box>
                    </Stack>
                ) : null}
            </Dialog>

            <Dialog
                id="promotion-reject-dialog"
                title={t("page.promotion_approval_panel.reject_dialog.title", { defaultValue: "Reject Promotion" })}
                open={rejectingPromo != null}
                onClose={closeRejectDialog}
                width="small"
            >
                {rejectingPromo ? (
                    <Stack space="large">
                        <Text>
                            {`Reject "${rejectingPromo.promo_name}" (${rejectingPromo.promo_code})? You may optionally record a reason below.`}
                        </Text>
                        <Textarea
                            id="promo-reject-reason"
                            label={t("page.promotion_approval_panel.reject_dialog.reason_label", { defaultValue: "Reason (optional)" })}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.currentTarget.value)}
                            placeholder={t("page.promotion_approval_panel.reject_dialog.reason_placeholder", {
                                defaultValue: "e.g. Discount percentage exceeds policy limit",
                            })}
                            lines={3}
                        />
                        <Box display="flex" justifyContent="flexEnd">
                            <Inline space="small">
                                <Button variant="ghost" onClick={closeRejectDialog} loading={submitting}>
                                    {t("page.promotion_approval_panel.button.cancel", { defaultValue: "Cancel" })}
                                </Button>
                                <Button variant="solid" tone="critical" onClick={handleConfirmReject} loading={submitting}>
                                    {t("page.promotion_approval_panel.button.confirm_reject", { defaultValue: "Reject Promotion" })}
                                </Button>
                            </Inline>
                        </Box>
                    </Stack>
                ) : null}
            </Dialog>
        </>
    );
};

export default PromotionApprovalPanel;
